import { useEffect, useState } from "react";

/**
 * Loads jQuery + Dokobit's isign.frame.js once, then exposes the global `Isign`
 * helper. The Gateway iframe (id="isign-gateway") uses postMessage; this script
 * wires the success/error callbacks and auto-adjusts iframe height.
 *
 * Scripts are served from the prod gateway host (gateway.dokobit.com) regardless
 * of sandbox/prod signing — the helper is host-agnostic and just talks to
 * whichever iframe origin is mounted.
 */

const JQUERY_SRC = "https://code.jquery.com/jquery-3.7.1.min.js";
const ISIGN_SRC = "https://gateway.dokobit.com/js/isign.frame.js";

/** Subset of Dokobit's global `Isign` helper (from isign.frame.js) that we use. */
interface IsignGateway {
  onSignSuccess?: () => void;
  onSignError?: (err: unknown) => void;
  /** When false, Dokobit stops auto-resizing the iframe to its content height. */
  adjustHeight?: boolean;
  PostMessage?: { init?: () => void };
}

declare global {
  interface Window {
    Isign?: IsignGateway;
    jQuery?: unknown;
  }
}

function loadScript(src: string, isLoaded: () => boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isLoaded()) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error(`failed to load ${src}`)));
      if (isLoaded()) resolve();
      return;
    }
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.addEventListener("load", () => resolve());
    el.addEventListener("error", () => reject(new Error(`failed to load ${src}`)));
    document.head.appendChild(el);
  });
}

export function useDokobitFrame(enabled: boolean): { ready: boolean; error: string | null } {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || ready) return;
    let cancelled = false;
    (async () => {
      try {
        await loadScript(JQUERY_SRC, () => typeof window.jQuery !== "undefined");
        await loadScript(ISIGN_SRC, () => typeof window.Isign !== "undefined");
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load signing widget");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, ready]);

  return { ready, error };
}
